import type { PickupKind } from './types.js';

export interface PickupInfo {
  color: number;
  icon: string;
  label: string;
  name: string;
  effect: string;
  model: string;
}

export const PICKUP_INFO: Record<PickupKind, PickupInfo> = {
  repair: {
    color: 0x72f59a,
    icon: '修',
    label: '回血',
    name: '维修泡泡',
    effect: '立即恢复 2 点生命',
    model: 'repair-cross',
  },
  shield: {
    color: 0x55e8ff,
    icon: '盾',
    label: '护盾',
    name: '超级护盾',
    // 与实际机制一致：每次格挡额外消耗 1.2s 护盾时间（见 TankGame.damageTank），
    // 旧文案「抵挡来袭伤害 7.5 秒」被玩家理解为 7.5s 无敌，误导（2026-07-26 修正）。
    effect: '护盾 7.5 秒，每次格挡消耗 1.2 秒',
    model: 'shield-dome',
  },
  rapid: {
    color: 0xffd14e,
    icon: '速',
    label: '速射',
    name: '汽水速射',
    effect: '大幅缩短装填时间 8.5 秒',
    model: 'rapid-rocket',
  },
  overdrive: {
    color: 0xff7a3d,
    icon: '超',
    label: '超载',
    name: '涡轮超载',
    effect: '加速、增伤，并让炮弹多反弹一次',
    model: 'turbo-engine',
  },
  mine: {
    color: 0xff5d78,
    icon: '雷',
    label: '地雷',
    name: '泡泡地雷',
    effect: '在当前位置布设区域控制地雷',
    model: 'spike-mine',
  },
  drone: {
    color: 0xb46dff,
    icon: '机',
    label: '无人机',
    name: '辅助无人机',
    effect: '召唤自动射击无人机 8.8 秒',
    model: 'mini-drone',
  },
};

export const PICKUP_KINDS = Object.keys(PICKUP_INFO) as PickupKind[];

// ---------------------------------------------------------------------------
// 道具效果数值（2026-07-26 重构：原散落在 TankGame 各处的魔法数字集中到此，
// 调平衡只改这一个文件；文案见上方 PICKUP_INFO，改数值时记得同步 effect 描述）。
// ---------------------------------------------------------------------------

export const PICKUP_TUNING = {
  /** 维修泡泡：立即回复的生命值。 */
  repair: { heal: 2 },
  /** 超级护盾：持续秒数（受击消耗 1.2s，见 TankGame.damageTank）。 */
  shield: { durationSec: 7.5 },
  /** 汽水速射：持续秒数与装填间隔倍率（越小越快）。 */
  rapid: { durationSec: 8.5, fireIntervalFactor: 0.48 },
  /**
   * 涡轮超载：持续秒数 + 移速/弹速倍率 + 伤害加成 + 额外反弹次数 + 装填倍率。
   * 协议注意：PvP fire 事件的 speed/damage 发送生效后数值，strong 位复现视觉。
   */
  overdrive: {
    durationSec: 6.8,
    speedFactor: 1.2,
    projectileSpeedFactor: 1.15,
    damageBonus: 1,
    extraBounces: 1,
    fireIntervalFactor: 0.68,
  },
  /** 泡泡地雷：伤害/寿命/布防延迟/触发半径（同队免疫见 TankGame.updateMines）。 */
  mine: { damage: 3, lifeSec: 12, armSec: 0.35, triggerRadius: 2.15 },
  /** 辅助无人机：持续秒数、拾取时冷却重置、射击节奏与弹道参数（索敌受 PvP 迷雾约束）。 */
  drone: {
    durationSec: 8.8,
    resumeCooldownSec: 0.28,
    shotCooldownSec: 0.92,
    searchRange: 18,
    shotSpeed: 15.5,
    shotDamage: 1,
    shotLifeSec: 2.8,
    shotRadius: 0.18,
  },
} as const;

/**
 * 掉落权重（PvE 击杀掉落与补给点刷新共用；PvP 槽位种子抽取也走这里，
 * 两端版本一致即结果一致）。2026-07-26 口径：进攻型（雷/无人机/速射/超载）整体上调。
 */
export const PICKUP_WEIGHTS: Record<PickupKind, number> = {
  repair: 1.0,
  shield: 0.9,
  rapid: 1.05,
  overdrive: 0.95,
  mine: 1.15,
  drone: 1.05,
};
